import { type DateTime } from "luxon";
import type { DateRange } from "@mui/x-date-pickers-pro";
import type { IntlShape } from "react-intl";

import { formatRelativeTimeText } from "@/components/reporting/last-updated";

export const isSameDateTime = (dt1: DateTime | null | undefined, dt2: DateTime | null | undefined): boolean => {
	if (!dt1 && !dt2) return true;
	if (!dt1 || !dt2) return false;
	if (!dt1.isValid || !dt2.isValid) return false;
	return dt1.toMillis() === dt2.toMillis() && dt1.zoneName === dt2.zoneName;
};

export const isSameDateRange = (
	range1: DateRange<DateTime> | null | undefined,
	range2: DateRange<DateTime> | null | undefined,
): boolean => {
	if (!range1 && !range2) return true;
	if (!range1 || !range2) return false;
	return isSameDateTime(range1[0], range2[0]) && isSameDateTime(range1[1], range2[1]);
};

export const formatUri = (dateTime: DateTime | null): string => {
	if (!dateTime?.isValid) {
		return "";
	}
	return dateTime.toFormat("yyyy-MM-dd'T'HH:mm:ss");
};

export const formatDuration = (seconds: number | null | undefined, intl: IntlShape, decimals: number = 1): string => {
	if (seconds == null || isNaN(seconds)) {
		return intl.formatMessage({ id: "em-dash", defaultMessage: "—" });
	}

	const absSeconds = Math.abs(seconds);

	// Less than 60 seconds → show in seconds
	if (absSeconds < 60) {
		return intl.formatMessage(
			{ id: "DurationSeconds", defaultMessage: "{value} sec" },
			{ value: absSeconds.toFixed(decimals) },
		);
	}

	// Less than 60 minutes (3600 seconds) → show in minutes
	if (absSeconds < 3600) {
		const minutes = absSeconds / 60;
		return intl.formatMessage(
			{ id: "DurationMinutes", defaultMessage: "{value} min" },
			{ value: minutes.toFixed(decimals) },
		);
	}

	// Less than 24 hours (86400 seconds) → show in hours
	if (absSeconds < 86400) {
		const hours = absSeconds / 3600;
		return intl.formatMessage(
			{ id: "DurationHours", defaultMessage: "{value} hrs" },
			{ value: hours.toFixed(decimals) },
		);
	}

	// 24 hours or more → show in days
	const days = absSeconds / 86400;
	return intl.formatMessage({ id: "DurationDays", defaultMessage: "{value} days" }, { value: days.toFixed(decimals) });
};

export const formatRelativeTime = (dateString: string | null | undefined, intl: IntlShape): string => {
	if (!dateString) {
		return intl.formatMessage({ id: "Never", defaultMessage: "Never" });
	}

	return (
		formatRelativeTimeText(dateString, intl, {
			includeSeconds: true,
			includeWeeks: true,
			includeYears: true,
			monthCalculation: "duration",
			thresholds: { days: 7 },
		}) ?? intl.formatMessage({ id: "Never", defaultMessage: "Never" })
	);
};
